import { z } from 'zod';
import { COMMISSIONING_EVENT_SOURCE_TYPES } from '../types/commissioning';
import type { CommissioningVariable, CommissioningVariableComputeConfig } from '../types/commissioning';
import { commissioningThresholdMeasureSchema, commissioningThresholdMeasurementSchema } from './commissioning-threshold-measures';

/** Source eligibility is shared by the editor and the existing preparation
 * path. A paid result is never a substitute for an observed raw inventory. */
export function isCommissioningObservationSourceVariable(
  variable: Pick<CommissioningVariable, 'is_computed' | 'source_kind' | 'resolver_key'>,
): boolean {
  return !variable.is_computed && variable.source_kind !== 'configurator_result'
    && !/^(period_aggregate|threshold_state|store_observation)\./.test(variable.resolver_key);
}

export type CommissioningObservationMetricDefinition = Pick<CommissioningVariable,
  'resolver_key' | 'is_computed' | 'data_type' | 'cardinality' | 'compute_config'>;

/** Effective aggregation of a period metric: the stored compute_config, or the
 * implicit period default of the three legacy resolver keys. Contract only. */
export function commissioningObservationMetricConfig(
  definition: Pick<CommissioningObservationMetricDefinition, 'resolver_key' | 'compute_config'>,
): CommissioningVariableComputeConfig | null {
  if (definition.compute_config) return definition.compute_config;
  const aggregation = definition.resolver_key === 'period_aggregate.valenza_sum' ? 'sum'
    : definition.resolver_key === 'period_aggregate.line_count' ? 'count'
      : definition.resolver_key === 'period_aggregate.line_total_sum' ? 'line_total_sum' : null;
  return aggregation ? { window: 'period', aggregation } : null;
}

/** Which numeric period metrics can observe a store. Shared by the editor
 * options, the publication preflight and the runtime measure so the three
 * never disagree; no fact or amount is evaluated here. */
export function isCommissioningStoreObservationMetric(definition: CommissioningObservationMetricDefinition): boolean {
  const config = commissioningObservationMetricConfig(definition);
  return definition.is_computed && definition.cardinality === 'scalar'
    && ['number', 'currency', 'percent'].includes(definition.data_type)
    && definition.resolver_key.startsWith('period_aggregate.')
    && config?.aggregation !== 'cross_ref'
    && (config !== null || definition.resolver_key === 'period_aggregate.tgt_pct');
}

/** Instance/assignment/member context, NEVER a reusable template's store IDs.
 * The target and counting basis belong to the observed KPI, not the paid owner. */
export const commissioningStoreObservationSchema = z.object({
  storeId: z.string().uuid(),
  driverIds: z.array(z.string().uuid()).min(1).max(1000),
  sourceTypes: z.array(z.enum(COMMISSIONING_EVENT_SOURCE_TYPES)).min(1).max(3),
  window: z.enum(['period', 'rolling_30d', 'rolling_90d']),
  target: z.number().finite().positive().nullable(),
  base: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('event_count') }).strict(),
    z.object({ kind: z.literal('variable'), code: z.string().min(1).max(100) }).strict(),
  ]),
}).strict().superRefine((value, ctx) => {
  for (const key of ['driverIds', 'sourceTypes'] as const) {
    if (new Set(value[key]).size !== value[key].length) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: [key], message: 'Le sorgenti devono essere distinte',
    });
  }
});
export type CommissioningStoreObservation = z.infer<typeof commissioningStoreObservationSchema>;

/** A root condition chooses exactly WHICH numeric variables observe a store.
 * Other variables in that same condition keep their original paid context. */
export const commissioningNumericObservationBindingsSchema = z.record(
  z.string().uuid(),
  z.record(z.string().min(1).max(100), commissioningStoreObservationSchema)
    .refine((value) => Object.keys(value).length > 0 && Object.keys(value).length <= 16,
      'Scegli da una a sedici variabili osservate per condizione'),
).refine((value) => Object.keys(value).length <= 32, 'Sono ammesse al massimo 32 condizioni osservazionali');
export type CommissioningNumericObservationBindings = z.infer<typeof commissioningNumericObservationBindingsSchema>;

/** Metadata only: type compatibility does not certify a payable result. */
export const commissioningNumericObservationOptionsSchema = z.object({
  functionId: z.string().uuid(), version: z.number().int().positive(),
  variables: z.array(z.object({
    code: z.string().min(1),
    versions: z.array(z.number().int().positive()).min(1),
    requiresTarget: z.boolean(), compatible: z.boolean(), reason: z.string().nullable(),
  }).strict()),
}).strict();
export type CommissioningNumericObservationOptions = z.infer<typeof commissioningNumericObservationOptionsSchema>;

export const commissioningNumericObservationEvidenceSchema = z.object({
  variableCode: z.string().min(1).max(100), variableVersion: z.number().int().positive(),
  binding: commissioningStoreObservationSchema,
  configurationRevision: z.string().regex(/^[a-f0-9]{64}$/),
  windowStart: z.string().date(), windowEnd: z.string().date(),
  observedAt: z.string().datetime({ offset: true }),
  cutoff: z.object({
    occurredAt: z.string().datetime({ offset: true }), eventId: z.string().uuid(),
  }).strict().optional(),
  sourceRevision: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
  measure: commissioningThresholdMeasureSchema,
  unit: commissioningThresholdMeasurementSchema.shape.unit.nullable(),
}).strict().superRefine((value, ctx) => {
  if (value.windowStart > value.windowEnd) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['windowEnd'], message: 'La finestra osservata è invertita',
  });
  if (value.measure.validity.status === 'valid' && (value.sourceRevision === null || value.unit === null)) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['sourceRevision'], message: 'Una misura valida richiede un inventario verificato',
  });
});
export type CommissioningNumericObservationEvidence = z.infer<typeof commissioningNumericObservationEvidenceSchema>;

export const commissioningNumericObservationSnapshotSchema = z.record(
  z.string().uuid(), z.array(commissioningNumericObservationEvidenceSchema),
).superRefine((roots, ctx) => {
  for (const [root, observations] of Object.entries(roots)) {
    const versions = new Set<string>();
    observations.forEach((observation, index) => {
      const key = `${observation.variableCode}@${observation.variableVersion}`;
      if (versions.has(key)) ctx.addIssue({
        code: z.ZodIssueCode.custom, path: [root, index], message: 'Versione osservata duplicata',
      });
      versions.add(key);
    });
  }
});
export type CommissioningNumericObservationSnapshot = z.infer<typeof commissioningNumericObservationSnapshotSchema>;
